import { supabase } from '../lib/supabase';
import { calcularTotales, filtrarQueAfectanBalance } from '../utils/balanceCalculations';

export interface IndicadoresFinancieros {
  totalAportes: number;
  balanceAgregado: number;
  movimientosPeriodo: number;
  totalIngresos: number;
  totalEgresos: number;
  /** Entradas por financiamiento de deudas: fuera de los ingresos, dentro del balance. */
  totalFinanciamiento: number;
  totalCuotaIglesiaRecaudada: number;
}

export interface IndicadoresOperativos {
  totalMiembrosActivos: number;
  totalLumisiales: number;
  campanasActivas: number;
  actividadesEconomicasActivas: number;
}

export interface LumisialStats {
  id: string;
  nombre: string;
  ciudad: string;
  diocesis: string;
  miembrosActivos: number;
  balance: number;
  ingresos: number;
  egresos: number;
  cuotaIglesiaRecaudada: number;
}

export interface DiocesisStatsGroup {
  diocesisId: string;
  diocesisNombre: string;
  diocesisTipo: 'diocesis' | 'prefectura';
  miembrosActivos: number;
  balance: number;
  ingresos: number;
  egresos: number;
  cuotaIglesiaRecaudada: number;
  lumisiales: LumisialStats[];
}

export interface ProvinciaStatsGroup {
  provinciaId: string;
  provinciaNombre: string;
  miembrosActivos: number;
  balance: number;
  ingresos: number;
  egresos: number;
  cuotaIglesiaRecaudada: number;
  diocesis: DiocesisStatsGroup[];
}

export interface HierarchicalStats {
  provincias: ProvinciaStatsGroup[];
}

interface MovimientoStats {
  monto: number | string;
  tipo: string;
  origen_tipo: string;
  categoria_aporte: string | null;
  afecta_balance: boolean | null;
}

export interface FiltrosZona {
  provinciaEclesiasticaId?: string;
  diocesisId?: string;
  ciudad?: string;
  region?: string;
  fechaDesde?: string;
  fechaHasta?: string;
}

/**
 * Aplica filtros geográficos a una query de lumisiales.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function aplicarFiltrosGeograficos(query: any, filtros: FiltrosZona) {
  let filteredQuery = query;

  if (filtros.provinciaEclesiasticaId) {
    filteredQuery = filteredQuery.eq('provincia_eclesiastica_id', filtros.provinciaEclesiasticaId);
  }

  if (filtros.diocesisId) {
    filteredQuery = filteredQuery.eq('diocesis_id', filtros.diocesisId);
  }

  if (filtros.ciudad) {
    filteredQuery = filteredQuery.ilike('ciudad', `%${filtros.ciudad}%`);
  }

  if (filtros.region) {
    filteredQuery = filteredQuery.ilike('region', `%${filtros.region}%`);
  }

  return filteredQuery;
}

/**
 * Aplica filtros de fecha a una query de movimientos.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function aplicarFiltrosFecha(query: any, filtros: FiltrosZona) {
  let filteredQuery = query;

  if (filtros.fechaDesde) {
    filteredQuery = filteredQuery.gte('fecha', filtros.fechaDesde);
  }

  if (filtros.fechaHasta) {
    filteredQuery = filteredQuery.lte('fecha', filtros.fechaHasta);
  }

  return filteredQuery;
}

/**
 * Calcula estadísticas financieras a partir de movimientos.
 */
function calcularEstadisticasFinancieras(movimientos: MovimientoStats[] | null) {
  const cuotaIglesiaRecaudada = movimientos
    ?.filter((m) => m.tipo === 'ingreso' && m.origen_tipo === 'miembro' && m.categoria_aporte === 'cuota_iglesia')
    .reduce((sum, m) => sum + Number(m.monto), 0) || 0;

  const afectanBalance = filtrarQueAfectanBalance(movimientos ?? []);
  // El financiamiento de una deuda no es ingreso propio, pero sí suma al balance.
  const { ingresos: totalIngresos, financiamiento: totalFinanciamiento } = calcularTotales(afectanBalance);

  // Mismo filtro que los ingresos: el dinero en tránsito no es gasto de la entidad. Sin esta
  // simetría el balance del tablero sale por debajo del real.
  const totalEgresos = afectanBalance
    .filter((m) => m.tipo === 'egreso')
    .reduce((sum, m) => sum + Math.abs(Number(m.monto)), 0);

  return {
    cuotaIglesiaRecaudada,
    totalIngresos,
    totalEgresos,
    totalFinanciamiento,
    balance: totalIngresos + totalFinanciamiento - totalEgresos,
  };
}

class ZonaStatsService {
  async getIndicadoresFinancieros(filtros: FiltrosZona): Promise<IndicadoresFinancieros> {
    try {
      const lumisialesQuery = aplicarFiltrosGeograficos(
        supabase.from('lumisiales').select('id').eq('activo', true),
        filtros
      );

      const { data: lumisiales, error: lumisialesError } = await lumisialesQuery;

      if (lumisialesError) throw lumisialesError;

      const lumisialIds = lumisiales?.map((l: { id: string }) => l.id) || [];

      if (lumisialIds.length === 0) {
        return {
          totalAportes: 0,
          balanceAgregado: 0,
          movimientosPeriodo: 0,
          totalIngresos: 0,
          totalEgresos: 0,
          totalFinanciamiento: 0,
          totalCuotaIglesiaRecaudada: 0,
        };
      }

      const movimientosQuery = aplicarFiltrosFecha(
        supabase
          .from('movimientos')
          .select('monto, tipo, origen_tipo, categoria_aporte, afecta_balance')
          .in('lumisial_id', lumisialIds)
          .eq('anulado', false),
        filtros
      );

      const { data: movimientos, error: movimientosError } = await movimientosQuery;

      if (movimientosError) throw movimientosError;

      const { cuotaIglesiaRecaudada, totalIngresos, totalEgresos, totalFinanciamiento, balance } =
        calcularEstadisticasFinancieras(movimientos);

      const aportesQuery = aplicarFiltrosFecha(
        supabase
          .from('movimientos')
          .select('monto')
          .in('lumisial_id', lumisialIds)
          .eq('tipo', 'ingreso')
          .eq('origen_tipo', 'miembro')
          .eq('anulado', false),
        filtros
      );

      const { data: aportes, error: aportesError } = await aportesQuery;

      if (aportesError) throw aportesError;

      const totalAportes = aportes?.reduce((sum: number, a: { monto: number }) => sum + Number(a.monto), 0) || 0;

      return {
        totalAportes,
        balanceAgregado: balance,
        movimientosPeriodo: movimientos?.length || 0,
        totalIngresos,
        totalEgresos,
        totalFinanciamiento,
        totalCuotaIglesiaRecaudada: cuotaIglesiaRecaudada,
      };
    } catch (error) {
      console.error('Error al obtener indicadores financieros:', error);
      throw error;
    }
  }

  async getIndicadoresOperativos(filtros: FiltrosZona): Promise<IndicadoresOperativos> {
    try {
      const lumisialesQuery = aplicarFiltrosGeograficos(
        supabase.from('lumisiales').select('id, activo').eq('activo', true),
        filtros
      );

      const { data: lumisiales, error: lumisialesError } = await lumisialesQuery;

      if (lumisialesError) throw lumisialesError;

      const lumisialIds = lumisiales?.map((l: { id: string }) => l.id) || [];
      const totalLumisiales = lumisiales?.length || 0;

      if (lumisialIds.length === 0) {
        return {
          totalMiembrosActivos: 0,
          totalLumisiales: 0,
          campanasActivas: 0,
          actividadesEconomicasActivas: 0,
        };
      }

      const { data: miembros, error: miembrosError } = await supabase
          .from('miembros')
          .select('id')
          .in('lumisial_actual_id', lumisialIds)
          .eq('estado_miembro', 'activo');

      if (miembrosError) throw miembrosError;

      const { data: campanas, error: campanasError } = await supabase
        .from('campañas')
        .select('id')
        .in('lumisial_id', lumisialIds)
        .in('estado', ['planificacion', 'en_curso']);

      if (campanasError) throw campanasError;

      const { data: actividades, error: actividadesError } = await supabase
        .from('actividades_economicas')
        .select('id')
        .in('lumisial_id', lumisialIds)
        .in('estado', ['planificacion', 'en_curso']);

      if (actividadesError) throw actividadesError;

      return {
        totalMiembrosActivos: miembros?.length || 0,
        totalLumisiales,
        campanasActivas: campanas?.length || 0,
        actividadesEconomicasActivas: actividades?.length || 0,
      };
    } catch (error) {
      console.error('Error al obtener indicadores operativos:', error);
      throw error;
    }
  }

  async getLumisialesStats(filtros: FiltrosZona): Promise<LumisialStats[]> {
    try {
      const lumisialesQuery = aplicarFiltrosGeograficos(
        supabase
          .from('lumisiales')
          .select(`
            id,
            nombre,
            ciudad,
            activo,
            diocesis:diocesis_id(nombre)
          `)
          .eq('activo', true)
          .order('nombre'),
        filtros
      );

      const { data: lumisiales, error: lumisialesError } = await lumisialesQuery;

      if (lumisialesError) throw lumisialesError;

      if (!lumisiales || lumisiales.length === 0) {
        return [];
      }

      const statsPromises = lumisiales.map(async (lumisial: { id: string; nombre: string; ciudad: string; diocesis?: { nombre: string } | null }) => {
        const movimientosQuery = aplicarFiltrosFecha(
          supabase
            .from('movimientos')
            .select('monto, tipo, origen_tipo, categoria_aporte, afecta_balance')
            .eq('lumisial_id', lumisial.id)
            .eq('anulado', false),
          filtros
        );

        const { data: movimientos } = await movimientosQuery;

        const { cuotaIglesiaRecaudada, totalIngresos, totalEgresos, balance } =
          calcularEstadisticasFinancieras(movimientos);

        const { data: miembros } = await supabase
          .from('miembros')
          .select('id')
          .eq('lumisial_actual_id', lumisial.id)
          .eq('estado_miembro', 'activo');

        return {
          id: lumisial.id,
          nombre: lumisial.nombre,
          ciudad: lumisial.ciudad,
          diocesis: lumisial.diocesis?.nombre || 'Sin asignar',
          miembrosActivos: miembros?.length || 0,
          balance,
          ingresos: totalIngresos,
          egresos: totalEgresos,
          cuotaIglesiaRecaudada,
        };
      });

      const stats = await Promise.all(statsPromises);

      return stats;
    } catch (error) {
      console.error('Error al obtener estadísticas de Lumisiales:', error);
      throw error;
    }
  }

  async getLumisialesStatsGrouped(filtros: FiltrosZona): Promise<HierarchicalStats> {
    try {
      const lumisialesQuery = aplicarFiltrosGeograficos(
        supabase
          .from('lumisiales')
          .select(`
            id,
            nombre,
            ciudad,
            activo,
            provincia_eclesiastica_id,
            diocesis_id,
            provincia_eclesiastica:provincia_eclesiastica_id(id, nombre),
            diocesis:diocesis_id(id, nombre, tipo)
          `)
          .eq('activo', true)
          .order('nombre'),
        filtros
      );

      const { data: lumisiales, error: lumisialesError } = await lumisialesQuery;

      if (lumisialesError) throw lumisialesError;

      if (!lumisiales || lumisiales.length === 0) {
        return { provincias: [] };
      }

      const statsPromises = lumisiales.map(async (lumisial: {
        id: string; nombre: string; ciudad: string;
        provincia_eclesiastica_id: string | null;
        diocesis_id: string | null;
        provincia_eclesiastica?: { id: string; nombre: string } | null;
        diocesis?: { id: string; nombre: string; tipo: string } | null;
      }) => {
        const movimientosQuery = aplicarFiltrosFecha(
          supabase
            .from('movimientos')
            .select('monto, tipo, origen_tipo, categoria_aporte, afecta_balance')
            .eq('lumisial_id', lumisial.id)
            .eq('anulado', false),
          filtros
        );

        const { data: movimientos } = await movimientosQuery;

        const { cuotaIglesiaRecaudada, totalIngresos, totalEgresos, balance } =
          calcularEstadisticasFinancieras(movimientos);

        const { data: miembros } = await supabase
          .from('miembros')
          .select('id')
          .eq('lumisial_actual_id', lumisial.id)
          .eq('estado_miembro', 'activo');

        return {
          lumisial,
          stats: {
            id: lumisial.id,
            nombre: lumisial.nombre,
            ciudad: lumisial.ciudad,
            diocesis: lumisial.diocesis?.nombre || 'Sin asignar',
            miembrosActivos: miembros?.length || 0,
            balance,
            ingresos: totalIngresos,
            egresos: totalEgresos,
            cuotaIglesiaRecaudada,
          }
        };
      });

      const statsWithLumisiales = await Promise.all(statsPromises);

      const provinciasMap = new Map<string, ProvinciaStatsGroup>();

      statsWithLumisiales.forEach(({ lumisial, stats }) => {
        const provinciaId = lumisial.provincia_eclesiastica_id || 'sin-provincia';
        const provinciaNombre = lumisial.provincia_eclesiastica?.nombre || 'Sin Provincia';
        const diocesisId = lumisial.diocesis_id?.toString() || 'sin-diocesis';
        const diocesisNombre = lumisial.diocesis?.nombre || 'Sin Diócesis';
        const diocesisTipo = (lumisial.diocesis?.tipo as 'diocesis' | 'prefectura') || 'diocesis';

        if (!provinciasMap.has(provinciaId)) {
          provinciasMap.set(provinciaId, {
            provinciaId,
            provinciaNombre,
            miembrosActivos: 0,
            balance: 0,
            ingresos: 0,
            egresos: 0,
            cuotaIglesiaRecaudada: 0,
            diocesis: []
          });
        }

        const provincia = provinciasMap.get(provinciaId)!;
        let diocesis = provincia.diocesis.find(d => d.diocesisId === diocesisId);

        if (!diocesis) {
          diocesis = {
            diocesisId,
            diocesisNombre,
            diocesisTipo,
            miembrosActivos: 0,
            balance: 0,
            ingresos: 0,
            egresos: 0,
            cuotaIglesiaRecaudada: 0,
            lumisiales: []
          };
          provincia.diocesis.push(diocesis);
        }

        diocesis.lumisiales.push(stats);
        diocesis.miembrosActivos += stats.miembrosActivos;
        diocesis.balance += stats.balance;
        diocesis.ingresos += stats.ingresos;
        diocesis.egresos += stats.egresos;
        diocesis.cuotaIglesiaRecaudada += stats.cuotaIglesiaRecaudada;

        provincia.miembrosActivos += stats.miembrosActivos;
        provincia.balance += stats.balance;
        provincia.ingresos += stats.ingresos;
        provincia.egresos += stats.egresos;
        provincia.cuotaIglesiaRecaudada += stats.cuotaIglesiaRecaudada;
      });

      const provincias = Array.from(provinciasMap.values());

      return { provincias };
    } catch (error) {
      console.error('Error al obtener estadísticas jerárquicas de Lumisiales:', error);
      throw error;
    }
  }
}

export const zonaStatsService = new ZonaStatsService();
